import React, { useCallback, useRef, useState } from "react";
import { IconCopy, IconTrash } from "@tabler/icons-react";
import { withCacheBuster } from "@/components/common/adCard/Media";
import { RiLoopRightLine } from "@remixicon/react";

// Calculate rotated rectangle corners for rotation-aware snapping
const getRotatedCorners = (
  x: number,
  y: number,
  width: number,
  height: number,
  rotationDeg: number,
) => {
  const centerX = x + width / 2;
  const centerY = y + height / 2;
  const rotationRad = (rotationDeg * Math.PI) / 180;

  // Calculate the four corners relative to center
  const corners = [
    { x: -width / 2, y: -height / 2 }, // top-left
    { x: width / 2, y: -height / 2 }, // top-right
    { x: width / 2, y: height / 2 }, // bottom-right
    { x: -width / 2, y: height / 2 }, // bottom-left
  ];

  // Rotate each corner around the center
  const rotatedCorners = corners.map((corner) => {
    const cos = Math.cos(rotationRad);
    const sin = Math.sin(rotationRad);
    return {
      x: centerX + corner.x * cos - corner.y * sin,
      y: centerY + corner.x * sin + corner.y * cos,
    };
  });

  return rotatedCorners;
};

// Get the extreme points for rotation-aware snapping
const getRotatedExtremes = (
  x: number,
  y: number,
  width: number,
  height: number,
  rotationDeg: number,
) => {
  const corners = getRotatedCorners(x, y, width, height, rotationDeg);

  // Find the extreme points
  const leftmost = Math.min(...corners.map((c) => c.x));
  const rightmost = Math.max(...corners.map((c) => c.x));
  const topmost = Math.min(...corners.map((c) => c.y));
  const bottommost = Math.max(...corners.map((c) => c.y));

  return {
    left: leftmost,
    right: rightmost,
    top: topmost,
    bottom: bottommost,
    centerX: x + width / 2,
    centerY: y + height / 2,
  };
};

export interface ImageBox {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  src: string;
  isSelected: boolean;
}

interface AddImageRequest {
  token: number;
  src: string;
  naturalWidth: number;
  naturalHeight: number;
}

interface KonvaImageBoxProps {
  imageRef: React.RefObject<HTMLImageElement>;
  onBoxesChange?: (boxes: ImageBox[]) => void;
  onSelectionChange?: (selected: ImageBox | null) => void;
  onInteractionStateChange?: (isActive: boolean) => void;
  initialImageBoxes?: ImageBox[];
  deselectRequestToken?: number;
  addImageRequest?: AddImageRequest | null;
  addOnNextClick?: AddImageRequest | null;
  onConsumeAddOnNextClick?: () => void;
  // Peer elements for snapping (text boxes in the same image)
  peerTextBoxes?: Array<{
    id: string;
    x: number;
    y: number;
    width: number;
    // approximate height is fine for snapping; parent can pass measured
    // heights; if missing we'll fall back to a reasonable value
    // height is optional to keep prop flexible
    height?: number;
    rotation?: number;
  }>;
}

const KonvaImageBox: React.FC<KonvaImageBoxProps> = ({
  imageRef,
  onBoxesChange,
  onSelectionChange,
  onInteractionStateChange,
  initialImageBoxes,
  deselectRequestToken,
  addImageRequest,
  addOnNextClick,
  onConsumeAddOnNextClick,
  peerTextBoxes,
}) => {
  const [boxes, setBoxes] = useState<ImageBox[]>(initialImageBoxes || []);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [clipRect, setClipRect] = useState<{
    left: number;
    top: number;
    clientLeft: number;
    clientTop: number;
    width: number;
    height: number;
  }>({ left: 0, top: 0, clientLeft: 0, clientTop: 0, width: 0, height: 0 });
  const [clipBorderRadius, setClipBorderRadius] = useState<string>("0px");
  const [interactingId, setInteractingId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const suppressNextOverlayClickRef = useRef<boolean>(false);
  // Active alignment guides during drag
  const [activeGuideV, setActiveGuideV] = useState<{
    x: number;
    kind: "container" | "peer";
  } | null>(null);
  const [activeGuideH, setActiveGuideH] = useState<{
    y: number;
    kind: "container" | "peer";
  } | null>(null);

  // Compute image bounds relative to overlay for clipping
  React.useEffect(() => {
    const computeClip = () => {
      const img = imageRef?.current;
      const overlay = overlayRef.current;
      if (!img || !overlay) return;
      const left = img.offsetLeft;
      const top = img.offsetTop;
      const clientLeft = img.getBoundingClientRect().left;
      const clientTop = img.getBoundingClientRect().top;
      const width = img.offsetWidth;
      const height = img.offsetHeight;
      setClipRect({ left, top, clientLeft, clientTop, width, height });
      const container = overlay.parentElement;
      if (container) {
        const cs = getComputedStyle(container);
        setClipBorderRadius(cs.borderRadius || "0px");
      }
    };
    computeClip();
    const onResize = () => computeClip();
    window.addEventListener("resize", onResize);
    const img = imageRef?.current;
    if (img) img.addEventListener("load", computeClip);
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => computeClip());
      if (img) ro.observe(img);
      if (overlayRef.current) ro.observe(overlayRef.current);
    }
    return () => {
      window.removeEventListener("resize", onResize);
      if (img) img.removeEventListener("load", computeClip);
      if (ro) ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageRef, imageRef.current]);

  // Sync with incoming initial boxes
  React.useEffect(() => {
    if (!initialImageBoxes) return;
    setBoxes(initialImageBoxes);
    const selected = initialImageBoxes.find((b) => b.isSelected);
    if (selected) {
      setSelectedId(selected.id);
    } else {
      setSelectedId(null);
    }
  }, [initialImageBoxes]);

  // Deselect on request
  const lastDeselectTokenRef = useRef<number>(0);
  React.useEffect(() => {
    if (!deselectRequestToken) return;
    if (lastDeselectTokenRef.current === deselectRequestToken) return;
    lastDeselectTokenRef.current = deselectRequestToken;
    setBoxes((prev) => prev.map((b) => ({ ...b, isSelected: false })));
    setSelectedId(null);
    onSelectionChange?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deselectRequestToken]);

  // Handle add image request (centered)
  const lastAddTokenRef = useRef<number>(0);
  React.useEffect(() => {
    const req = addImageRequest;
    if (!req) return;
    if (lastAddTokenRef.current === req.token) return;
    lastAddTokenRef.current = req.token;
    const { left, top, width, height } = clipRect;
    if (width === 0 || height === 0) return;
    const aspect =
      req.naturalWidth > 0 && req.naturalHeight > 0
        ? req.naturalWidth / req.naturalHeight
        : 1;
    const defaultWidth = 200;
    const defaultHeight = Math.max(
      50,
      Math.round(defaultWidth / Math.max(0.0001, aspect)),
    );
    const x = left + width / 2 - defaultWidth / 2;
    const y = top + height / 2 - defaultHeight / 2;
    const newBox: ImageBox = {
      id: `imgbox_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      x,
      y,
      width: defaultWidth,
      height: defaultHeight,
      rotation: 0,
      src: req.src,
      isSelected: true,
    };
    setBoxes((prev) => {
      const cleared = prev.map((b) => ({ ...b, isSelected: false }));
      const updated = [...cleared, newBox];
      onBoxesChange?.(updated);
      return updated;
    });
    setSelectedId(newBox.id);
    onSelectionChange?.(newBox);
  }, [addImageRequest, clipRect, onBoxesChange, onSelectionChange]);

  const updateBox = useCallback(
    (id: string, updates: Partial<ImageBox>) => {
      setBoxes((prev) => {
        let selected: ImageBox | null = null;
        const updated = prev.map((b) => {
          if (b.id === id) {
            const merged = { ...b, ...updates } as ImageBox;
            if (merged.id === selectedId && merged.isSelected !== false)
              selected = merged;
            return merged;
          }
          return b;
        });
        onBoxesChange?.(updated);
        if (selected) onSelectionChange?.(selected);
        return updated;
      });
    },
    [onBoxesChange, onSelectionChange, selectedId],
  );

  const selectBox = useCallback(
    (id: string) => {
      setBoxes((prev) => {
        const updated = prev.map((b) => ({ ...b, isSelected: b.id === id }));
        onBoxesChange?.(updated);
        const sel = updated.find((b) => b.id === id) || null;
        onSelectionChange?.(sel);
        return updated;
      });
      setSelectedId(id);
    },
    [onBoxesChange, onSelectionChange],
  );

  const duplicateBox = useCallback(
    (id: string) => {
      setBoxes((prev) => {
        const src = prev.find((b) => b.id === id);
        if (!src) return prev;
        const dup: ImageBox = {
          ...src,
          id: `imgbox_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
          x: src.x + 20,
          y: src.y + 20,
          isSelected: true,
        };
        const cleared = prev.map((b) => ({ ...b, isSelected: false }));
        const updated = [...cleared, dup];
        onBoxesChange?.(updated);
        onSelectionChange?.(dup);
        setSelectedId(dup.id);
        return updated;
      });
    },
    [onBoxesChange, onSelectionChange],
  );

  const deleteBox = useCallback(
    (id: string) => {
      setBoxes((prev) => {
        const updated = prev.filter((b) => b.id !== id);
        onBoxesChange?.(updated);
        onSelectionChange?.(null);
        setSelectedId(null);
        return updated;
      });
    },
    [onBoxesChange, onSelectionChange],
  );

  // Keyboard shortcuts: Delete/Backspace to remove selected; Escape to deselect; Arrow keys to move
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const fallbackSelectedId =
        selectedId || (boxes.find((b) => b.isSelected)?.id ?? null);
      if (!fallbackSelectedId) return;

      if (e.key === "Delete" || e.key === "Backspace") {
        setBoxes((prev) => {
          const updated = prev.filter((b) => b.id !== fallbackSelectedId);
          onBoxesChange?.(updated);
          return updated;
        });
        setSelectedId(null);
        onSelectionChange?.(null);
        return;
      }

      if (e.key === "Escape") {
        setBoxes((prev) => prev.map((b) => ({ ...b, isSelected: false })));
        setSelectedId(null);
        onSelectionChange?.(null);
        return;
      }

      if (
        e.key === "ArrowUp" ||
        e.key === "ArrowDown" ||
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight"
      ) {
        // Do not move while interacting (dragging/resizing)
        if (interactingId) return;

        // Avoid interfering with text inputs or content editable fields
        const active = document.activeElement as HTMLElement | null;
        const tag = active?.tagName?.toLowerCase();
        if (tag === "input" || tag === "textarea" || active?.isContentEditable) return;

        e.preventDefault();
        const moveDistance = e.shiftKey ? 10 : 1;
        let deltaX = 0;
        let deltaY = 0;
        switch (e.key) {
          case "ArrowUp":
            deltaY = -moveDistance;
            break;
          case "ArrowDown":
            deltaY = moveDistance;
            break;
          case "ArrowLeft":
            deltaX = -moveDistance;
            break;
          case "ArrowRight":
            deltaX = moveDistance;
            break;
        }
        setBoxes((prev) => {
          const updated = prev.map((b) =>
            b.id === fallbackSelectedId ? { ...b, x: b.x + deltaX, y: b.y + deltaY } : b,
          );
          onBoxesChange?.(updated);
          return updated;
        });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedId, boxes, onBoxesChange, onSelectionChange, interactingId]);

  // Drag/resize state
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState<string | null>(null);
  const [isRotating, setIsRotating] = useState(false);
  const [dragStart, setDragStart] = useState({
    x: 0,
    y: 0,
    startX: 0,
    startY: 0,
    id: "",
  });
  const [resizeStart, setResizeStart] = useState({
    x: 0,
    y: 0,
    startX: 0,
    startY: 0,
    startW: 0,
    startH: 0,
    id: "",
  });
  const [rotationStart, setRotationStart] = useState({
    x: 0,
    y: 0,
    startRotation: 0,
    centerX: 0,
    centerY: 0,
    id: "",
    initialAngle: 0,
  });

  const onMouseDownDrag = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    selectBox(id);
    onInteractionStateChange?.(true);
    const b = boxes.find((bb) => bb.id === id);
    if (!b) return;
    setIsDragging(true);
    setInteractingId(id);
    setDragStart({ x: e.clientX, y: e.clientY, startX: b.x, startY: b.y, id });
  };

  const onMouseDownResize = (e: React.MouseEvent, id: string, dir: string) => {
    e.preventDefault();
    e.stopPropagation();
    selectBox(id);
    onInteractionStateChange?.(true);
    const b = boxes.find((bb) => bb.id === id);
    if (!b) return;
    setIsResizing(dir);
    setInteractingId(id);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      startX: b.x,
      startY: b.y,
      startW: b.width,
      startH: b.height,
      id,
    });
    diagonalBasisRef.current = null;
  };

  const onMouseDownRotate = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    selectBox(id);
    onInteractionStateChange?.(true);
    const b = boxes.find((bb) => bb.id === id);
    if (!b) return;
    setIsRotating(true);
    setInteractingId(id);

    // Calculate the center of the image box in global viewport coordinates
    // b.x/y are relative to image, so add image position (clipRect.left/top)
    const centerX = clipRect.clientLeft + b.x + b.width / 2;
    const centerY = clipRect.clientTop + b.y + b.height / 2;

    // Calculate initial angle from center to mouse position
    const initialVectorX = e.clientX - centerX;
    const initialVectorY = e.clientY - centerY;
    const initialAngleRad = Math.atan2(initialVectorX, -initialVectorY);
    const initialAngleDeg = initialAngleRad * (180 / Math.PI);

    setRotationStart({
      x: e.clientX,
      y: e.clientY,
      startRotation: b.rotation,
      centerX,
      centerY,
      id,
      initialAngle: initialAngleDeg,
    });
  };

  const diagonalBasisRef = useRef<"width" | "height" | null>(null);

  // Minimum size thresholds for showing resize handles
  const MIN_SIZE_FOR_CORNER_HANDLES = 100; // px - show corner handles when larger than this
  const MIN_SIZE_FOR_SIDE_HANDLES = 100; // px - show side handles when larger than this

  // Helper functions to determine handle visibility
  const shouldShowCornerHandles = (box: ImageBox) => {
    return (
      box.width >= MIN_SIZE_FOR_CORNER_HANDLES &&
      box.height >= MIN_SIZE_FOR_CORNER_HANDLES
    );
  };

  const shouldShowSideHandles = (box: ImageBox) => {
    return (
      box.width >= MIN_SIZE_FOR_SIDE_HANDLES && box.height >= MIN_SIZE_FOR_SIDE_HANDLES
    );
  };

  React.useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      if (isDragging && dragStart.id) {
        const dx = e.clientX - dragStart.x;
        const dy = e.clientY - dragStart.y;
        const current = boxes.find((bb) => bb.id === dragStart.id);
        const proposedX = dragStart.startX + dx;
        const proposedY = dragStart.startY + dy;
        if (!current) {
          updateBox(dragStart.id, { x: proposedX, y: proposedY });
          return;
        }

        // Build guide candidates
        const SNAP_EPS = 6; // px
        const containerVerticalGuides = [
          { x: clipRect.left, kind: "container" as const },
          { x: clipRect.left + clipRect.width / 2, kind: "container" as const },
          { x: clipRect.left + clipRect.width, kind: "container" as const },
        ];
        const containerHorizontalGuides = [
          { y: clipRect.top, kind: "container" as const },
          { y: clipRect.top + clipRect.height / 2, kind: "container" as const },
          { y: clipRect.top + clipRect.height, kind: "container" as const },
        ];

        const peerRects: Array<{
          id: string;
          x: number;
          y: number;
          width: number;
          height: number;
          rotation?: number;
        }> = [];
        // Other image boxes
        for (const b of boxes) {
          if (b.id === dragStart.id) continue;
          peerRects.push({
            id: b.id,
            x: b.x,
            y: b.y,
            width: b.width,
            height: b.height,
            rotation: b.rotation,
          });
        }
        // Text boxes from parent
        for (const tb of peerTextBoxes || []) {
          const h = Math.max(10, tb.height || 0);
          peerRects.push({
            id: tb.id,
            x: tb.x,
            y: tb.y,
            width: tb.width,
            height: h,
            rotation: tb.rotation || 0,
          });
        }

        const peerVerticalGuides = peerRects.flatMap((r) => {
          const rotation = r.rotation || 0;
          if (rotation === 0) {
            // No rotation - use simple bounding box guides
            return [
              { x: r.x, kind: "peer" as const },
              { x: r.x + r.width / 2, kind: "peer" as const },
              { x: r.x + r.width, kind: "peer" as const },
            ];
          } else {
            // Rotation - use rotation-aware extremes
            const extremes = getRotatedExtremes(r.x, r.y, r.width, r.height, rotation);
            return [
              { x: extremes.left, kind: "peer" as const },
              { x: extremes.centerX, kind: "peer" as const },
              { x: extremes.right, kind: "peer" as const },
            ];
          }
        });
        const peerHorizontalGuides = peerRects.flatMap((r) => {
          const rotation = r.rotation || 0;
          if (rotation === 0) {
            // No rotation - use simple bounding box guides
            return [
              { y: r.y, kind: "peer" as const },
              { y: r.y + r.height / 2, kind: "peer" as const },
              { y: r.y + r.height, kind: "peer" as const },
            ];
          } else {
            // Rotation - use rotation-aware extremes
            const extremes = getRotatedExtremes(r.x, r.y, r.width, r.height, rotation);
            return [
              { y: extremes.top, kind: "peer" as const },
              { y: extremes.centerY, kind: "peer" as const },
              { y: extremes.bottom, kind: "peer" as const },
            ];
          }
        });

        const allV = [...containerVerticalGuides, ...peerVerticalGuides];
        const allH = [...containerHorizontalGuides, ...peerHorizontalGuides];

        // Use rotation-aware extremes for snapping
        const extremes = getRotatedExtremes(
          proposedX,
          proposedY,
          current.width,
          current.height,
          current.rotation || 0,
        );

        const curLeft = extremes.left;
        const curCenterX = extremes.centerX;
        const curRight = extremes.right;
        const curTop = extremes.top;
        const curCenterY = extremes.centerY;
        const curBottom = extremes.bottom;

        let bestVX = proposedX;
        let bestVGuide: { x: number; kind: "container" | "peer" } | null = null;
        {
          const candidates: Array<{
            dist: number;
            snapX: number;
            guide: { x: number; kind: "container" | "peer" };
          }> = [];
          for (const g of allV) {
            // For rotated boxes, snap to the extreme points (leftmost, center, rightmost)
            candidates.push({
              dist: Math.abs(g.x - curLeft),
              snapX: g.x - (curLeft - proposedX),
              guide: g,
            });
            candidates.push({
              dist: Math.abs(g.x - curCenterX),
              snapX: g.x - (curCenterX - proposedX),
              guide: g,
            });
            candidates.push({
              dist: Math.abs(g.x - curRight),
              snapX: g.x - (curRight - proposedX),
              guide: g,
            });
          }
          candidates.sort((a, b) => a.dist - b.dist);
          const best = candidates[0];
          if (best && best.dist <= SNAP_EPS) {
            bestVX = best.snapX;
            bestVGuide = { x: best.guide.x, kind: best.guide.kind };
          }
        }

        let bestVY = proposedY;
        let bestHGuide: { y: number; kind: "container" | "peer" } | null = null;
        {
          const candidates: Array<{
            dist: number;
            snapY: number;
            guide: { y: number; kind: "container" | "peer" };
          }> = [];
          for (const g of allH) {
            // For rotated boxes, snap to the extreme points (topmost, center, bottommost)
            candidates.push({
              dist: Math.abs(g.y - curTop),
              snapY: g.y - (curTop - proposedY),
              guide: g,
            });
            candidates.push({
              dist: Math.abs(g.y - curCenterY),
              snapY: g.y - (curCenterY - proposedY),
              guide: g,
            });
            candidates.push({
              dist: Math.abs(g.y - curBottom),
              snapY: g.y - (curBottom - proposedY),
              guide: g,
            });
          }
          candidates.sort((a, b) => a.dist - b.dist);
          const best = candidates[0];
          if (best && best.dist <= SNAP_EPS) {
            bestVY = best.snapY;
            bestHGuide = { y: best.guide.y, kind: best.guide.kind };
          }
        }

        setActiveGuideV(bestVGuide);
        setActiveGuideH(bestHGuide);
        updateBox(dragStart.id, { x: bestVX, y: bestVY });
      } else if (isResizing && resizeStart.id) {
        const dx = e.clientX - resizeStart.x;
        const dy = e.clientY - resizeStart.y;
        const dir = isResizing;
        const MIN_W = 1;
        const MIN_H = 1;

        // Build guide candidates for snapping during resize
        const SNAP_EPS = 6; // px
        const containerVerticalGuides = [
          { x: clipRect.left, kind: "container" as const },
          { x: clipRect.left + clipRect.width / 2, kind: "container" as const },
          { x: clipRect.left + clipRect.width, kind: "container" as const },
        ];
        const containerHorizontalGuides = [
          { y: clipRect.top, kind: "container" as const },
          { y: clipRect.top + clipRect.height / 2, kind: "container" as const },
          { y: clipRect.top + clipRect.height, kind: "container" as const },
        ];

        const peerRects: Array<{
          id: string;
          x: number;
          y: number;
          width: number;
          height: number;
          rotation?: number;
        }> = [];
        // Other image boxes
        for (const b of boxes) {
          if (b.id === resizeStart.id) continue;
          peerRects.push({
            id: b.id,
            x: b.x,
            y: b.y,
            width: b.width,
            height: b.height,
            rotation: b.rotation,
          });
        }
        // Text boxes from parent
        for (const tb of peerTextBoxes || []) {
          const h = Math.max(10, tb.height || 0);
          peerRects.push({
            id: tb.id,
            x: tb.x,
            y: tb.y,
            width: tb.width,
            height: h,
            rotation: tb.rotation || 0,
          });
        }

        const peerVerticalGuides = peerRects.flatMap((r) => {
          const rotation = r.rotation || 0;
          if (rotation === 0) {
            // No rotation - use simple bounding box guides
            return [
              { x: r.x, kind: "peer" as const },
              { x: r.x + r.width / 2, kind: "peer" as const },
              { x: r.x + r.width, kind: "peer" as const },
            ];
          } else {
            // Rotation - use rotation-aware extremes
            const extremes = getRotatedExtremes(r.x, r.y, r.width, r.height, rotation);
            return [
              { x: extremes.left, kind: "peer" as const },
              { x: extremes.centerX, kind: "peer" as const },
              { x: extremes.right, kind: "peer" as const },
            ];
          }
        });
        const peerHorizontalGuides = peerRects.flatMap((r) => {
          const rotation = r.rotation || 0;
          if (rotation === 0) {
            // No rotation - use simple bounding box guides
            return [
              { y: r.y, kind: "peer" as const },
              { y: r.y + r.height / 2, kind: "peer" as const },
              { y: r.y + r.height, kind: "peer" as const },
            ];
          } else {
            // Rotation - use rotation-aware extremes
            const extremes = getRotatedExtremes(r.x, r.y, r.width, r.height, rotation);
            return [
              { y: extremes.top, kind: "peer" as const },
              { y: extremes.centerY, kind: "peer" as const },
              { y: extremes.bottom, kind: "peer" as const },
            ];
          }
        });

        const allV = [...containerVerticalGuides, ...peerVerticalGuides];
        const allH = [...containerHorizontalGuides, ...peerHorizontalGuides];

        if (dir === "se" || dir === "sw" || dir === "ne" || dir === "nw") {
          const aspect =
            resizeStart.startW > 0 && resizeStart.startH > 0
              ? resizeStart.startW / resizeStart.startH
              : 1;
          const applyMinWithAspectFromWidth = (w: number) => {
            const wClamped = Math.max(MIN_W, w);
            let hCalc = wClamped / Math.max(0.0001, aspect);
            if (hCalc < MIN_H) {
              hCalc = MIN_H;
              return { w: hCalc * aspect, h: hCalc };
            }
            return { w: wClamped, h: hCalc };
          };
          const applyMinWithAspectFromHeight = (h: number) => {
            const hClamped = Math.max(MIN_H, h);
            let wCalc = hClamped * aspect;
            if (wCalc < MIN_W) {
              wCalc = MIN_W;
              return { w: wCalc, h: wCalc / Math.max(0.0001, aspect) };
            }
            return { w: wCalc, h: hClamped };
          };

          if (!diagonalBasisRef.current) {
            diagonalBasisRef.current = Math.abs(dx) >= Math.abs(dy) ? "width" : "height";
          }

          let w = resizeStart.startW;
          let h = resizeStart.startH;
          let x = resizeStart.startX;
          let y = resizeStart.startY;

          // Calculate new dimensions based on mouse movement
          if (diagonalBasisRef.current === "width") {
            if (dir === "se" || dir === "ne") {
              const { w: ww, h: hh } = applyMinWithAspectFromWidth(
                resizeStart.startW + dx,
              );
              w = ww;
              h = hh;
              // For SE: keep top-left fixed, adjust bottom-right
              // For NE: keep bottom-left fixed, adjust top-right
              if (dir === "ne") {
                y = resizeStart.startY + (resizeStart.startH - h);
              }
            } else {
              // For SW: keep top-right fixed, adjust bottom-left
              // For NW: keep bottom-right fixed, adjust top-left
              const { w: ww, h: hh } = applyMinWithAspectFromWidth(
                resizeStart.startW - dx,
              );
              w = ww;
              h = hh;
              x = resizeStart.startX + (resizeStart.startW - w);
              if (dir === "nw") {
                y = resizeStart.startY + (resizeStart.startH - h);
              }
            }
          } else {
            if (dir === "se" || dir === "sw") {
              const { w: ww, h: hh } = applyMinWithAspectFromHeight(
                resizeStart.startH + dy,
              );
              w = ww;
              h = hh;
              // For SE: keep top-left fixed, adjust bottom-right
              // For SW: keep top-right fixed, adjust bottom-left
              if (dir === "sw") {
                x = resizeStart.startX + (resizeStart.startW - w);
              }
            } else {
              // For NE: keep bottom-left fixed, adjust top-right
              // For NW: keep bottom-right fixed, adjust top-left
              const { w: ww, h: hh } = applyMinWithAspectFromHeight(
                resizeStart.startH - dy,
              );
              w = ww;
              h = hh;
              y = resizeStart.startY + (resizeStart.startH - h);
              if (dir === "nw") {
                x = resizeStart.startX + (resizeStart.startW - w);
              }
            }
          }

          // Apply rotation-aware snapping - maintain aspect ratio while keeping opposite vertex fixed
          const currentBox = boxes.find((b) => b.id === resizeStart.id);
          const rotation = currentBox?.rotation || 0;

          // Calculate the opposite vertex position that should remain fixed
          let fixedX: number, fixedY: number;
          if (dir === "se") {
            fixedX = resizeStart.startX;
            fixedY = resizeStart.startY;
          } else if (dir === "ne") {
            fixedX = resizeStart.startX;
            fixedY = resizeStart.startY + resizeStart.startH;
          } else if (dir === "sw") {
            fixedX = resizeStart.startX + resizeStart.startW;
            fixedY = resizeStart.startY;
          } else {
            // NW: bottom-right is fixed
            fixedX = resizeStart.startX + resizeStart.startW;
            fixedY = resizeStart.startY + resizeStart.startH;
          }

          // Calculate extreme edges of the rotated box with proposed dimensions
          const proposedExtremes = getRotatedExtremes(x, y, w, h, rotation);

          // Find the best snap point for extreme edges
          let bestSnapDistance = Infinity;
          let bestSnapResult: {
            x: number;
            y: number;
            width: number;
            height: number;
            vGuide: { x: number; kind: "container" | "peer" } | null;
            hGuide: { y: number; kind: "container" | "peer" } | null;
          } | null = null;

          // Determine which extreme edges to check based on resize direction
          const checkRightEdge = dir === "se" || dir === "ne";
          const checkLeftEdge = dir === "sw" || dir === "nw";
          const checkBottomEdge = dir === "se" || dir === "sw";
          const checkTopEdge = dir === "ne" || dir === "nw";

          // Check snapping for appropriate vertical extreme edge
          if (checkRightEdge || checkLeftEdge) {
            for (const vg of allV) {
              const targetExtreme = checkRightEdge
                ? proposedExtremes.right
                : proposedExtremes.left;
              const distance = Math.abs(vg.x - targetExtreme);

              if (distance <= SNAP_EPS && distance < bestSnapDistance) {
                // Iteratively find dimensions that place the extreme edge at vg.x
                let testW = w;
                let testH = h;
                for (let i = 0; i < 10; i++) {
                  let testX = x,
                    testY = y;
                  if (dir === "se") {
                    testX = fixedX;
                    testY = fixedY;
                  } else if (dir === "ne") {
                    testX = fixedX;
                    testY = fixedY - testH;
                  } else if (dir === "sw") {
                    testX = fixedX - testW;
                    testY = fixedY;
                  } else {
                    testX = fixedX - testW;
                    testY = fixedY - testH;
                  }

                  const testExtremes = getRotatedExtremes(
                    testX,
                    testY,
                    testW,
                    testH,
                    rotation,
                  );
                  const currentExtreme = checkRightEdge
                    ? testExtremes.right
                    : testExtremes.left;
                  const error = vg.x - currentExtreme;

                  if (Math.abs(error) < 0.5) break;

                  // Adjust width proportionally
                  const widthAdjust = error * (checkRightEdge ? 0.5 : -0.5);
                  testW += widthAdjust;
                  testH = (testW * resizeStart.startH) / resizeStart.startW;

                  if (testW < MIN_W || testH < MIN_H) {
                    testW = Math.max(MIN_W, testW);
                    testH = Math.max(
                      MIN_H,
                      (testW * resizeStart.startH) / resizeStart.startW,
                    );
                    break;
                  }
                }

                if (testW >= MIN_W && testH >= MIN_H) {
                  let finalX = x,
                    finalY = y;
                  if (dir === "se") {
                    finalX = fixedX;
                    finalY = fixedY;
                  } else if (dir === "ne") {
                    finalX = fixedX;
                    finalY = fixedY - testH;
                  } else if (dir === "sw") {
                    finalX = fixedX - testW;
                    finalY = fixedY;
                  } else {
                    finalX = fixedX - testW;
                    finalY = fixedY - testH;
                  }

                  bestSnapDistance = distance;
                  bestSnapResult = {
                    x: finalX,
                    y: finalY,
                    width: testW,
                    height: testH,
                    vGuide: vg,
                    hGuide: null,
                  };
                }
              }
            }
          }

          // Check snapping for appropriate horizontal extreme edge
          if (checkTopEdge || checkBottomEdge) {
            for (const hg of allH) {
              const targetExtreme = checkBottomEdge
                ? proposedExtremes.bottom
                : proposedExtremes.top;
              const distance = Math.abs(hg.y - targetExtreme);

              if (distance <= SNAP_EPS && distance < bestSnapDistance) {
                // Iteratively find dimensions that place the extreme edge at hg.y
                let testW = w;
                let testH = h;
                for (let i = 0; i < 10; i++) {
                  let testX = x,
                    testY = y;
                  if (dir === "se") {
                    testX = fixedX;
                    testY = fixedY;
                  } else if (dir === "ne") {
                    testX = fixedX;
                    testY = fixedY - testH;
                  } else if (dir === "sw") {
                    testX = fixedX - testW;
                    testY = fixedY;
                  } else {
                    testX = fixedX - testW;
                    testY = fixedY - testH;
                  }

                  const testExtremes = getRotatedExtremes(
                    testX,
                    testY,
                    testW,
                    testH,
                    rotation,
                  );
                  const currentExtreme = checkBottomEdge
                    ? testExtremes.bottom
                    : testExtremes.top;
                  const error = hg.y - currentExtreme;

                  if (Math.abs(error) < 0.5) break;

                  // Adjust height proportionally
                  const heightAdjust = error * (checkBottomEdge ? 0.5 : -0.5);
                  testH += heightAdjust;
                  testW = (testH * resizeStart.startW) / resizeStart.startH;

                  if (testW < MIN_W || testH < MIN_H) {
                    testH = Math.max(MIN_H, testH);
                    testW = Math.max(
                      MIN_W,
                      (testH * resizeStart.startW) / resizeStart.startH,
                    );
                    break;
                  }
                }

                if (testW >= MIN_W && testH >= MIN_H) {
                  let finalX = x,
                    finalY = y;
                  if (dir === "se") {
                    finalX = fixedX;
                    finalY = fixedY;
                  } else if (dir === "ne") {
                    finalX = fixedX;
                    finalY = fixedY - testH;
                  } else if (dir === "sw") {
                    finalX = fixedX - testW;
                    finalY = fixedY;
                  } else {
                    finalX = fixedX - testW;
                    finalY = fixedY - testH;
                  }

                  bestSnapDistance = distance;
                  bestSnapResult = {
                    x: finalX,
                    y: finalY,
                    width: testW,
                    height: testH,
                    vGuide: null,
                    hGuide: hg,
                  };
                }
              }
            }
          }

          // Apply the best snap result if found, otherwise use proposed values
          if (bestSnapResult) {
            setActiveGuideV(bestSnapResult.vGuide);
            setActiveGuideH(bestSnapResult.hGuide);
            updateBox(resizeStart.id, {
              x: bestSnapResult.x,
              y: bestSnapResult.y,
              width: bestSnapResult.width,
              height: bestSnapResult.height,
            });
          } else {
            setActiveGuideV(null);
            setActiveGuideH(null);
            updateBox(resizeStart.id, { x, y, width: w, height: h });
          }
        } else if (dir === "e") {
          // Right side resize with rotation-aware snapping
          const currentBox = boxes.find((b) => b.id === resizeStart.id);
          const rotation = currentBox?.rotation || 0;
          const proposedWidth = Math.max(MIN_W, resizeStart.startW + dx);

          // Calculate extreme edges with proposed width
          const proposedExtremes = getRotatedExtremes(
            resizeStart.startX,
            resizeStart.startY,
            proposedWidth,
            resizeStart.startH,
            rotation,
          );

          // Find closest snap for right extreme edge
          let bestDist = Infinity;
          let bestGuide: { x: number; kind: "container" | "peer" } | null = null;
          let bestWidth = proposedWidth;

          for (const g of allV) {
            const dist = Math.abs(g.x - proposedExtremes.right);
            if (dist <= SNAP_EPS && dist < bestDist) {
              // Iteratively find width that places right edge at g.x
              let testWidth = proposedWidth;
              for (let i = 0; i < 10; i++) {
                const testExtremes = getRotatedExtremes(
                  resizeStart.startX,
                  resizeStart.startY,
                  testWidth,
                  resizeStart.startH,
                  rotation,
                );
                const error = g.x - testExtremes.right;
                if (Math.abs(error) < 0.5) break;
                testWidth += error * 0.5;
                if (testWidth < MIN_W) {
                  testWidth = MIN_W;
                  break;
                }
              }
              if (testWidth >= MIN_W) {
                bestDist = dist;
                bestWidth = testWidth;
                bestGuide = { x: g.x, kind: g.kind };
              }
            }
          }

          setActiveGuideV(bestGuide);
          setActiveGuideH(null);
          updateBox(resizeStart.id, { width: bestWidth });
        } else if (dir === "w") {
          // Left side resize with rotation-aware snapping
          const currentBox = boxes.find((b) => b.id === resizeStart.id);
          const rotation = currentBox?.rotation || 0;
          const proposedWidth = Math.max(MIN_W, resizeStart.startW - dx);
          const fixedRightX = resizeStart.startX + resizeStart.startW;
          const proposedX = fixedRightX - proposedWidth;

          // Calculate extreme edges with proposed width
          const proposedExtremes = getRotatedExtremes(
            proposedX,
            resizeStart.startY,
            proposedWidth,
            resizeStart.startH,
            rotation,
          );

          // Find closest snap for left extreme edge
          let bestDist = Infinity;
          let bestGuide: { x: number; kind: "container" | "peer" } | null = null;
          let bestWidth = proposedWidth;
          let bestX = proposedX;

          for (const g of allV) {
            const dist = Math.abs(g.x - proposedExtremes.left);
            if (dist <= SNAP_EPS && dist < bestDist) {
              // Iteratively find width that places left edge at g.x
              let testWidth = proposedWidth;
              for (let i = 0; i < 10; i++) {
                const testX = fixedRightX - testWidth;
                const testExtremes = getRotatedExtremes(
                  testX,
                  resizeStart.startY,
                  testWidth,
                  resizeStart.startH,
                  rotation,
                );
                const error = g.x - testExtremes.left;
                if (Math.abs(error) < 0.5) break;
                testWidth -= error * 0.5;
                if (testWidth < MIN_W) {
                  testWidth = MIN_W;
                  break;
                }
              }
              if (testWidth >= MIN_W) {
                bestDist = dist;
                bestWidth = testWidth;
                bestX = fixedRightX - testWidth;
                bestGuide = { x: g.x, kind: g.kind };
              }
            }
          }

          setActiveGuideV(bestGuide);
          setActiveGuideH(null);
          updateBox(resizeStart.id, { width: bestWidth, x: bestX });
        } else if (dir === "s") {
          // Bottom side resize with rotation-aware snapping
          const currentBox = boxes.find((b) => b.id === resizeStart.id);
          const rotation = currentBox?.rotation || 0;
          const proposedHeight = Math.max(MIN_H, resizeStart.startH + dy);

          // Calculate extreme edges with proposed height
          const proposedExtremes = getRotatedExtremes(
            resizeStart.startX,
            resizeStart.startY,
            resizeStart.startW,
            proposedHeight,
            rotation,
          );

          // Find closest snap for bottom extreme edge
          let bestDist = Infinity;
          let bestGuide: { y: number; kind: "container" | "peer" } | null = null;
          let bestHeight = proposedHeight;

          for (const g of allH) {
            const dist = Math.abs(g.y - proposedExtremes.bottom);
            if (dist <= SNAP_EPS && dist < bestDist) {
              // Iteratively find height that places bottom edge at g.y
              let testHeight = proposedHeight;
              for (let i = 0; i < 10; i++) {
                const testExtremes = getRotatedExtremes(
                  resizeStart.startX,
                  resizeStart.startY,
                  resizeStart.startW,
                  testHeight,
                  rotation,
                );
                const error = g.y - testExtremes.bottom;
                if (Math.abs(error) < 0.5) break;
                testHeight += error * 0.5;
                if (testHeight < MIN_H) {
                  testHeight = MIN_H;
                  break;
                }
              }
              if (testHeight >= MIN_H) {
                bestDist = dist;
                bestHeight = testHeight;
                bestGuide = { y: g.y, kind: g.kind };
              }
            }
          }

          setActiveGuideV(null);
          setActiveGuideH(bestGuide);
          updateBox(resizeStart.id, { height: bestHeight });
        } else if (dir === "n") {
          // Top side resize with rotation-aware snapping
          const currentBox = boxes.find((b) => b.id === resizeStart.id);
          const rotation = currentBox?.rotation || 0;
          const proposedHeight = Math.max(MIN_H, resizeStart.startH - dy);
          const fixedBottomY = resizeStart.startY + resizeStart.startH;
          const proposedY = fixedBottomY - proposedHeight;

          // Calculate extreme edges with proposed height
          const proposedExtremes = getRotatedExtremes(
            resizeStart.startX,
            proposedY,
            resizeStart.startW,
            proposedHeight,
            rotation,
          );

          // Find closest snap for top extreme edge
          let bestDist = Infinity;
          let bestGuide: { y: number; kind: "container" | "peer" } | null = null;
          let bestHeight = proposedHeight;
          let bestY = proposedY;

          for (const g of allH) {
            const dist = Math.abs(g.y - proposedExtremes.top);
            if (dist <= SNAP_EPS && dist < bestDist) {
              // Iteratively find height that places top edge at g.y
              let testHeight = proposedHeight;
              for (let i = 0; i < 10; i++) {
                const testY = fixedBottomY - testHeight;
                const testExtremes = getRotatedExtremes(
                  resizeStart.startX,
                  testY,
                  resizeStart.startW,
                  testHeight,
                  rotation,
                );
                const error = g.y - testExtremes.top;
                if (Math.abs(error) < 0.5) break;
                testHeight -= error * 0.5;
                if (testHeight < MIN_H) {
                  testHeight = MIN_H;
                  break;
                }
              }
              if (testHeight >= MIN_H) {
                bestDist = dist;
                bestHeight = testHeight;
                bestY = fixedBottomY - testHeight;
                bestGuide = { y: g.y, kind: g.kind };
              }
            }
          }

          setActiveGuideV(null);
          setActiveGuideH(bestGuide);
          updateBox(resizeStart.id, { height: bestHeight, y: bestY });
        }
      } else if (isRotating && rotationStart.id) {
        // Calculate rotation based on angle from vertical line (north = 0°)
        const currentX = e.clientX;
        const currentY = e.clientY;

        // Calculate vector from image center to current mouse position
        const vectorX = currentX - rotationStart.centerX;
        const vectorY = currentY - rotationStart.centerY;

        // Calculate angle relative to vertical line (north = 0°)
        // atan2 gives angle from horizontal, so we need to adjust for vertical reference
        // -90° adjustment to make vertical line (0, -1) = 0°
        const currentAngleRad = Math.atan2(vectorX, -vectorY);
        const currentAngleDeg = currentAngleRad * (180 / Math.PI);

        // Calculate the rotation delta from the initial angle
        let rotationDelta = currentAngleDeg - rotationStart.initialAngle;

        // Normalize to [-180, 180]
        while (rotationDelta > 180) rotationDelta -= 360;
        while (rotationDelta < -180) rotationDelta += 360;

        // Apply rotation with 1-degree increments for precise control
        const snapIncrement = 1;
        const snappedRotationDelta =
          Math.round(rotationDelta / snapIncrement) * snapIncrement;

        let newRotation = rotationStart.startRotation + snappedRotationDelta;

        // Normalize final rotation to be between -180 and 180 degrees
        while (newRotation > 180) newRotation -= 360;
        while (newRotation < -180) newRotation += 360;

        updateBox(rotationStart.id, { rotation: newRotation });
      }
    };
    const handleUp = () => {
      if (isDragging || isResizing || isRotating) {
        setIsDragging(false);
        setIsResizing(null);
        setIsRotating(false);
        // Prevent the very next overlay click from deselecting immediately after drag/resize
        suppressNextOverlayClickRef.current = true;
        setTimeout(() => {
          suppressNextOverlayClickRef.current = false;
          onInteractionStateChange?.(false);
          setInteractingId(null);
        }, 150);
        setActiveGuideV(null);
        setActiveGuideH(null);
      }
    };
    if (isDragging || isResizing || isRotating) {
      document.addEventListener("mousemove", handleMove);
      document.addEventListener("mouseup", handleUp);
      const styleId = "global-cursor-override";
      let styleElement = document.getElementById(styleId) as HTMLStyleElement;
      if (!styleElement) {
        styleElement = document.createElement("style");
        styleElement.id = styleId;
        document.head.appendChild(styleElement);
      }
      const cursorStyle = (() => {
        if (isDragging) return "move";
        if (isRotating) return "grab";
        if (!isResizing) return "default";
        if (["e", "w"].includes(isResizing)) return "ew-resize";
        if (["n", "s"].includes(isResizing)) return "ns-resize";
        if (["ne", "sw"].includes(isResizing)) return "nesw-resize";
        if (["nw", "se"].includes(isResizing)) return "nwse-resize";
        return "default";
      })();
      styleElement.textContent = `
        *, *::before, *::after { cursor: ${cursorStyle} !important; }
      `;
      return () => {
        document.removeEventListener("mousemove", handleMove);
        document.removeEventListener("mouseup", handleUp);
        const el = document.getElementById(styleId);
        if (el) el.remove();
      };
    }
  }, [
    isDragging,
    isResizing,
    isRotating,
    dragStart,
    resizeStart,
    rotationStart,
    updateBox,
    onInteractionStateChange,
    boxes,
    clipRect.left,
    clipRect.width,
    clipRect.top,
    clipRect.height,
    peerTextBoxes,
  ]);

  return (
    <div
      ref={overlayRef}
      className="absolute inset-0 pointer-events-auto"
      data-image-overlay
      onClick={(e) => {
        // If an add-on-click is pending, place the image box at the click location
        if (addOnNextClick) {
          const ov = overlayRef.current;
          if (ov) {
            const rect = ov.getBoundingClientRect();
            const localX = e.clientX - rect.left;
            const localY = e.clientY - rect.top;
            const aspect =
              addOnNextClick.naturalWidth > 0 && addOnNextClick.naturalHeight > 0
                ? addOnNextClick.naturalWidth / addOnNextClick.naturalHeight
                : 1;
            const defaultWidth = 200;
            const defaultHeight = Math.max(
              50,
              Math.round(defaultWidth / Math.max(0.0001, aspect)),
            );
            // Position relative to the clipped image area, centered on click
            let x = clipRect.left + localX - defaultWidth / 2;
            let y = clipRect.top + localY - defaultHeight / 2;
            // Clamp inside the clip rect
            x = Math.min(
              Math.max(x, clipRect.left),
              clipRect.left + clipRect.width - defaultWidth,
            );
            y = Math.min(
              Math.max(y, clipRect.top),
              clipRect.top + clipRect.height - defaultHeight,
            );
            const newBox: ImageBox = {
              id: `imgbox_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
              x,
              y,
              width: defaultWidth,
              height: defaultHeight,
              rotation: 0,
              src: addOnNextClick.src,
              isSelected: true,
            };
            setBoxes((prev) => {
              const cleared = prev.map((b) => ({ ...b, isSelected: false }));
              const updated = [...cleared, newBox];
              onBoxesChange?.(updated);
              return updated;
            });
            setSelectedId(newBox.id);
            onSelectionChange?.(newBox);
            onConsumeAddOnNextClick?.();
            return; // Do not propagate to deselect flow
          }
        }
        if (interactingId) return;
        if (suppressNextOverlayClickRef.current) return;
        // Deselect all on background click
        setBoxes((prev) => prev.map((b) => ({ ...b, isSelected: false })));
        setSelectedId(null);
        onSelectionChange?.(null);
        onBoxesChange?.(boxes.map((b) => ({ ...b, isSelected: false })));
      }}
    >
      {/* Alignment guides - vertical */}
      {activeGuideV && (
        <div
          style={{
            position: "absolute",
            left: `${activeGuideV.x}px`,
            top: `${clipRect.top}px`,
            height: `${clipRect.height}px`,
            width: 0,
            borderLeft:
              activeGuideV.kind === "container"
                ? "1px solid #7e1cff"
                : "1px dashed #7e1cff",
            zIndex: 60,
            pointerEvents: "none",
          }}
        />
      )}
      {/* Alignment guides - horizontal */}
      {activeGuideH && (
        <div
          style={{
            position: "absolute",
            top: `${activeGuideH.y}px`,
            left: `${clipRect.left}px`,
            width: `${clipRect.width}px`,
            height: 0,
            borderTop:
              activeGuideH.kind === "container"
                ? "1px solid #7e1cff"
                : "1px dashed #7e1cff",
            zIndex: 60,
            pointerEvents: "none",
          }}
        />
      )}
      {/* Selection layer */}
      {boxes.map((b) => (
        <div
          key={b.id}
          data-image-box-id={b.id}
          style={{
            position: "absolute",
            left: `${b.x}px`,
            top: `${b.y}px`,
            width: `${b.width}px`,
            height: `${b.height}px`,
            transform: `rotate(${b.rotation}deg)`,
            outline: b.isSelected
              ? "2px solid #7C3AED"
              : hoveredId === b.id && !interactingId
                ? "1px solid #7C3AED"
                : "2px solid transparent",
            outlineOffset: "-1px",
            border: "none",
            zIndex: b.isSelected ? 49 : 2, // Same as text boxes, click-through logic handles selection
            userSelect: "none",
            background: "transparent",
          }}
          onMouseDown={(e) => onMouseDownDrag(e, b.id)}
          onClick={(e) => {
            // Get click position relative to the overlay container
            const overlay = e.currentTarget.closest(
              "[data-image-overlay]",
            ) as HTMLElement;
            if (!overlay) {
              e.stopPropagation();
              selectBox(b.id);
              return;
            }

            const overlayRect = overlay.getBoundingClientRect();
            const clickX = e.clientX - overlayRect.left;
            const clickY = e.clientY - overlayRect.top;
            const imageBoxArea = b.width * b.height;

            // Check if any peer text boxes are smaller and under this click
            for (const peer of peerTextBoxes || []) {
              const peerArea = peer.width * (peer.height || 20);
              // If peer is significantly smaller (less than 70% of image box area)
              if (peerArea < imageBoxArea * 0.7) {
                // Check if click is within the peer's bounds
                if (
                  clickX >= peer.x &&
                  clickX <= peer.x + peer.width &&
                  clickY >= peer.y &&
                  clickY <= peer.y + (peer.height || 20)
                ) {
                  // Find the text overlay (sibling of image overlay) and then the text box element
                  const textOverlay = overlay.parentElement?.querySelector(
                    "[data-text-overlay]",
                  ) as HTMLElement;
                  const textBoxElement = textOverlay?.querySelector(
                    `[data-text-box-id="${peer.id}"]`,
                  ) as HTMLElement;
                  if (textBoxElement) {
                    e.stopPropagation();
                    // Create a new click event and dispatch it to the text box
                    const syntheticClick = new MouseEvent("click", {
                      bubbles: true,
                      cancelable: true,
                      clientX: e.clientX,
                      clientY: e.clientY,
                    });
                    textBoxElement.dispatchEvent(syntheticClick);
                    return;
                  }
                }
              }
            }

            e.stopPropagation();
            selectBox(b.id);
          }}
          onMouseEnter={() => setHoveredId(b.id)}
          onMouseLeave={() => setHoveredId((prev) => (prev === b.id ? null : prev))}
        >
          {b.isSelected &&
            !isDragging &&
            !isResizing &&
            (() => {
              // Check if image box is near the top or bottom edge of the image
              const isNearTopEdge = b.y < 50; // 50px threshold from top
              const isNearBottomEdge = b.y + b.height > clipRect.height - 50; // 50px threshold from bottom
              // For rotations > 90 degrees, reverse the logic since the image box is visually upside down
              const isRotatedMoreThan90 = Math.abs(b.rotation) > 90;
              const shouldShowBelow =
                (isNearTopEdge && !isRotatedMoreThan90) ||
                (isNearBottomEdge && isRotatedMoreThan90);

              const buttonBarTop = shouldShowBelow ? `${b.height + 4}px` : "-44px";

              return (
                <div
                  style={{
                    position: "absolute",
                    top: buttonBarTop,
                    left: "50%",
                    transform: "translateX(-50%)",
                    display: "flex",
                    gap: "8px",
                    background: "white",
                    borderRadius: "12px",
                    padding: "6px 8px",
                    boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
                    border: "1px solid rgba(0,0,0,0.06)",
                    zIndex: 200,
                    color: "#0A0A0A",
                    pointerEvents: "auto",
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    title="Duplicate"
                    onClick={() => duplicateBox(b.id)}
                    style={{
                      width: 28,
                      height: 28,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 8,
                      border: "1px solid rgba(0,0,0,0.08)",
                    }}
                  >
                    <IconCopy size={16} />
                  </button>
                  <button
                    title="Delete"
                    onClick={() => deleteBox(b.id)}
                    style={{
                      width: 28,
                      height: 28,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 8,
                      border: "1px solid rgba(0,0,0,0.08)",
                    }}
                  >
                    <IconTrash size={16} />
                  </button>
                </div>
              );
            })()}

          {/* Corner handles - conditionally visible based on size */}
          {b.isSelected && (
            <>
              <>
                {(
                  [
                    ["nw", "-5px", "-5px"],
                    ["ne", "-5px", "-5px"],
                    ["sw", "-5px", "-5px"],
                    ["se", "-5px", "-5px"],
                  ] as const
                ).map(([dir, offsetY, offsetX]) => {
                  const pos: React.CSSProperties = {};
                  if (dir.includes("n")) pos.top = offsetY;
                  if (dir.includes("s")) pos.bottom = offsetY;
                  if (dir.includes("w")) pos.left = offsetX;
                  if (dir.includes("e")) pos.right = offsetX;
                  const isHidden = dir === "nw" ? false : !shouldShowCornerHandles(b);
                  return (
                    <div
                      key={dir}
                      style={{
                        position: "absolute",
                        visibility: isHidden ? "hidden" : "visible",
                        ...pos,
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        background: "#FFFFFF",
                        border: "2px solid #7C3AED",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
                        cursor: dir.includes("n")
                          ? dir.includes("w")
                            ? "nw-resize"
                            : "ne-resize"
                          : dir.includes("w")
                            ? "sw-resize"
                            : "se-resize",
                        zIndex: 20,
                      }}
                      onMouseDown={(e) => onMouseDownResize(e, b.id, dir)}
                    />
                  );
                })}
              </>
              {/* Side handles - conditionally visible based on size */}

              <>
                {(
                  [
                    ["n", "-5px", "50%", "translate(-50%, 0)", "ns-resize"],
                    ["s", "calc(100% - 6px)", "50%", "translate(-50%, 0)", "ns-resize"],
                    ["w", "50%", "-5px", "translate(0, -50%)", "ew-resize"],
                    ["e", "50%", "calc(100% - 6px)", "translate(0, -50%)", "ew-resize"],
                  ] as const
                ).map(([dir, , , , cursor]) => {
                  const isHidden = dir === "e" ? false : !shouldShowSideHandles(b);
                  return (
                    <div
                      key={dir}
                      style={{
                        position: "absolute",
                        visibility: isHidden ? "hidden" : "visible",
                        top: dir === "n" ? "-5px" : dir === "s" ? undefined : "50%",
                        bottom: dir === "s" ? "-5px" : undefined,
                        left: dir === "w" ? "-5px" : dir === "e" ? undefined : "50%",
                        right: dir === "e" ? "-5px" : undefined,
                        transform:
                          dir === "n" || dir === "s"
                            ? "translateX(-50%)"
                            : "translateY(-50%)",
                        width: dir === "n" || dir === "s" ? 16 : 8,
                        height: dir === "n" || dir === "s" ? 8 : 16,
                        borderRadius: 6,
                        background: "#FFFFFF",
                        border: "2px solid #7C3AED",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
                        cursor,
                        zIndex: 10,
                      }}
                      onMouseDown={(e) => onMouseDownResize(e, b.id, dir)}
                    />
                  );
                })}
              </>

              {/* Invisible resize zones for when handles are not visible - maintains functionality */}
              {!shouldShowCornerHandles(b) && (
                <>
                  {/* Invisible corner resize zones */}
                  {(["nw", "ne", "sw", "se"] as const).map((dir) => {
                    const offsetX = -5;
                    const offsetY = -5;
                    const pos: React.CSSProperties = {};
                    if (dir.includes("n")) pos.top = offsetY;
                    if (dir.includes("s")) pos.bottom = offsetY;
                    if (dir.includes("w")) pos.left = offsetX;
                    if (dir.includes("e")) pos.right = offsetX;
                    return (
                      <div
                        key={`invisible-${dir}`}
                        style={{
                          position: "absolute",
                          ...pos,
                          width: 20,
                          height: 20,
                          cursor: dir.includes("n")
                            ? dir.includes("w")
                              ? "nw-resize"
                              : "ne-resize"
                            : dir.includes("w")
                              ? "sw-resize"
                              : "se-resize",
                          zIndex: 20,
                          pointerEvents: "auto",
                        }}
                        onMouseDown={(e) => onMouseDownResize(e, b.id, dir)}
                      />
                    );
                  })}
                </>
              )}

              {!shouldShowSideHandles(b) && (
                <>
                  {/* Invisible side resize zones */}
                  {(["n", "s", "w", "e"] as const).map((dir) => (
                    <div
                      key={`invisible-${dir}`}
                      style={{
                        position: "absolute",
                        top: dir === "n" ? "-5px" : dir === "s" ? undefined : "0",
                        bottom: dir === "s" ? "-5px" : undefined,
                        left: dir === "w" ? "-5px" : dir === "e" ? undefined : "0",
                        right: dir === "e" ? "-5px" : undefined,
                        width: dir === "n" || dir === "s" ? "100%" : "10px",
                        height: dir === "n" || dir === "s" ? "10px" : "100%",
                        cursor: dir === "n" || dir === "s" ? "ns-resize" : "ew-resize",
                        zIndex: 20,
                        pointerEvents: "auto",
                      }}
                      onMouseDown={(e) => onMouseDownResize(e, b.id, dir)}
                    />
                  ))}
                </>
              )}

              {/* Rotation control button - positioned below the image box or button bar */}
              {!isDragging &&
                !isResizing &&
                (() => {
                  // Check if image box is near the top or bottom edge of the image
                  const isNearTopEdge = b.y < 50; // 50px threshold from top
                  const isNearBottomEdge = b.y + b.height > clipRect.height - 50; // 50px threshold from bottom
                  // For rotations > 90 degrees, reverse the logic since the image box is visually upside down
                  const isRotatedMoreThan90 = Math.abs(b.rotation) > 90;
                  const shouldShowBelow =
                    (isNearTopEdge && !isRotatedMoreThan90) ||
                    (isNearBottomEdge && isRotatedMoreThan90);

                  const rotationButtonTop = shouldShowBelow
                    ? `${b.height + 50}px`
                    : `${b.height + 10}px`;

                  return (
                    <div
                      style={{
                        position: "absolute",
                        top: rotationButtonTop,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "28px",
                        height: "28px",
                        background: "#7C3AED",
                        borderRadius: "50%",
                        border: "1px solid #FFFFFF",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        cursor: "grab",
                        zIndex: 20,
                        pointerEvents: "auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onMouseDown={(e) => onMouseDownRotate(e, b.id)}
                    >
                      {/* Show degree value during rotation, otherwise show rotation icon */}
                      {isRotating && rotationStart.id === b.id ? (
                        <span
                          style={{
                            color: "white",
                            fontSize: "10px",
                            fontWeight: "bold",
                            fontFamily: "monospace",
                          }}
                        >
                          {Math.round(b.rotation)}°
                        </span>
                      ) : (
                        <RiLoopRightLine size={14} className="text-white" />
                      )}
                    </div>
                  );
                })()}
            </>
          )}
        </div>
      ))}

      {/* Masked image layer */}
      <div
        style={{
          position: "absolute",
          left: `${clipRect.left}px`,
          top: `${clipRect.top}px`,
          width: `${clipRect.width}px`,
          height: `${clipRect.height}px`,
          overflow: "hidden",
          borderRadius: clipBorderRadius,
          zIndex: 5, // Below text content layer, click-through logic handles selection
          pointerEvents: "none",
        }}
      >
        {boxes.map((b) => (
          <img
            key={`content_${b.id}`}
            src={withCacheBuster(b.src)}
            crossOrigin="anonymous"
            alt="overlay"
            style={{
              position: "absolute",
              left: `${b.x - clipRect.left}px`,
              top: `${b.y - clipRect.top}px`,
              minWidth: `${b.width}px`,
              minHeight: `${b.height}px`,
              width: `${b.width}px`,
              height: `${b.height}px`,
              transform: `rotate(${b.rotation}deg)`,
              objectFit: "contain",
              userSelect: "none",
              pointerEvents: "none",
            }}
            draggable={false}
          />
        ))}
      </div>
    </div>
  );
};

export default KonvaImageBox;
